import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router';

import PropTypes from 'prop-types';
// TODO: DicomMetadataStore should be injected?
import { DicomMetadataStore, ServicesManager } from '@ohif/core';
import { DragAndDropProvider, ImageViewerProvider } from '@ohif/ui';
import { useQuery, useSearchParams } from '@hooks';
import ViewportGrid from '@components/ViewportGrid';
import Compose from './Compose';
import getStudies from './studiesList';

// Função para decodificar JWT sem verificação (apenas para extrair payload)
function decodeJWT(token) {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Erro ao decodificar JWT:', error);
    return null;
  }
}

/**
 * Initialize the route.
 *
 * @param props.servicesManager to read services from
 * @param props.studyInstanceUIDs for a list of studies to read
 * @param props.dataSource to read the data from
 * @param props.filters filters from query params to read the data from
 * @returns array of subscriptions to cancel
 */
function defaultRouteInit(
  { servicesManager, studyInstanceUIDs, dataSource, filters },
  hangingProtocolId
) {
  const {
    displaySetService,
    hangingProtocolService,
  } = servicesManager.services;

  const unsubscriptions = [];
  const {
    unsubscribe: instanceAddedUnsubscribe,
  } = DicomMetadataStore.subscribe(
    DicomMetadataStore.EVENTS.INSTANCES_ADDED,
    function ({ StudyInstanceUID, SeriesInstanceUID, madeInClient = false }) {
      const seriesMetadata = DicomMetadataStore.getSeries(
        StudyInstanceUID,
        SeriesInstanceUID
      );

      displaySetService.makeDisplaySets(seriesMetadata.instances, madeInClient);
    }
  );

  unsubscriptions.push(instanceAddedUnsubscribe);

  const allRetrieves = studyInstanceUIDs.map(StudyInstanceUID =>
    dataSource.retrieve.series.metadata({
      StudyInstanceUID,
      filters,
    })
  );

  // The hanging protocol matching service is fairly expensive to run multiple
  // times, and doesn't allow partial matches to be made (it will simply fail
  // to display anything if a required match fails), so we wait here until all metadata
  // is retrieved (which will synchronously trigger the display set creation)
  // until we run the hanging protocol matching service.

  Promise.allSettled(allRetrieves).then(() => {
    const displaySets = displaySetService.getActiveDisplaySets();

    if (!displaySets || !displaySets.length) {
      return;
    }

    // Gets the studies list to use
    const studies = getStudies(studyInstanceUIDs, displaySets);

    // study being displayed, and is thus the "active" study.
    const activeStudy = studies[0];

    // run the hanging protocol matching on the displaySets with the predefined
    // hanging protocol in the mode configuration
    hangingProtocolService.run(
      { studies, activeStudy, displaySets },
      hangingProtocolId
    );
  });

  return unsubscriptions;
}

export default function ModeRoute({
  mode,
  dataSourceName,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
}) {
  // Parse route params/querystring
  const location = useLocation();
  const query = useQuery();
  const params = useParams();
  const searchParams = useSearchParams();

  const runTimeHangingProtocolId = searchParams.get('hangingprotocolid');
  const [studyInstanceUIDs, setStudyInstanceUIDs] = useState<string[] | undefined>(undefined);
  const [layoutData, setLayoutData] = useState(null); // Estado para controlar o layout

  const locationRef = useRef(null);
  const isMounted = useRef(false);
  const isInitialized = useRef(false); // Flag para evitar múltiplas inicializações

  if (location !== locationRef.current) {
    setLayoutData(null); // Reset layout data quando location muda
    locationRef.current = location;
  }

  const {
    displaySetService,
    hangingProtocolService,
  } = (servicesManager as ServicesManager).services;

  const {
    extensions,
    sopClassHandlers,
    hotkeys: hotkeyObj,
    hangingProtocol,
  } = mode;
  // Preserve the old array interface for hotkeys
  const hotkeys = Array.isArray(hotkeyObj) ? hotkeyObj : hotkeyObj?.hotkeys;
  const hotkeyName = hotkeyObj?.name || 'hotkey-definitions-v2';

  if (dataSourceName === undefined) {
    dataSourceName = extensionManager.defaultDataSourceName;
  }

  extensionManager.setActiveDataSource(dataSourceName);

  const dataSource = extensionManager.getActiveDataSource()[0];

  // Only handling one route per mode for now
  const route = mode.routes[0];

  // For each extension, look up their context modules
  // TODO: move to extension manager.
  let contextModules = [];

  Object.keys(extensions).forEach(extensionId => {
    const allRegisteredModuleIds = Object.keys(extensionManager.modulesMap);
    const moduleIds = allRegisteredModuleIds.filter(id =>
      id.includes(`${extensionId}.contextModule.`)
    );

    if (!moduleIds || !moduleIds.length) {
      return;
    }

    const modules = moduleIds.map(extensionManager.getModuleEntry);
    contextModules = contextModules.concat(modules);
  });

  const contextModuleProviders = contextModules.map(a => a.provider);
  const CombinedContextProvider = ({ children }) =>
    Compose({ components: contextModuleProviders, children });

  function ViewportGridWithDataSource(props) {
    return ViewportGrid({ ...props, dataSource });
  }

  useEffect(() => {
    // Preventing state update for unmounted component
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      isInitialized.current = false;
    };
  }, []);

  // Extrair studyUid do JWT e inicializar dataSource
  useEffect(() => {
    const initializeDataSource = async (params: Record<string, any>, query: URLSearchParams) => {
      if (isInitialized.current) {
        return; // Evita múltiplas inicializações
      }

      try {
        let studyInstanceUIDs = [];

        // Verificar se os hooks estão disponíveis
        if (!searchParams || !query) {
          console.log('SearchParams ou query não disponíveis ainda, aguardando...');
          return;
        }

        // Tentar obter o token JWT da URL (query parameter ou route parameter)
        const jwtToken = searchParams.get('token') || params?.token || query.get('token');

        if (jwtToken) {
          console.log('Token JWT encontrado, decodificando...');
          const decodedToken = decodeJWT(jwtToken);

          if (decodedToken && decodedToken.studyUid) {
            // Converter o studyUid string para array
            studyInstanceUIDs = [decodedToken.studyUid as string];
            console.log('StudyUID extraído do JWT:', decodedToken.studyUid);
          } else {
            console.error('Token JWT não contém studyUid ou é inválido');
          }
        }

        // Se não conseguiu extrair do JWT, usar método original
        if (!studyInstanceUIDs.length && dataSource && dataSource.initialize) {
          try {
            console.log('Usando método de inicialização original...');
            const originalStudyUIDs = await dataSource.initialize({
              params: params || {},
              query: query || new URLSearchParams(),
            });
            studyInstanceUIDs = originalStudyUIDs || [];
          } catch (error) {
            console.error('Erro ao inicializar dataSource:', error);
          }
        }

        // Só atualizar se o componente ainda estiver montado e houver UIDs válidos
        if (isMounted.current && studyInstanceUIDs && studyInstanceUIDs.length > 0) {
          setStudyInstanceUIDs([...studyInstanceUIDs]);
          isInitialized.current = true;
        } else if (studyInstanceUIDs && studyInstanceUIDs.length === 0) {
          console.warn('Nenhum StudyInstanceUID encontrado');
        }
      } catch (error) {
        console.error('Erro na inicialização:', error);
      }
    };

    // Adicionar um pequeno delay para garantir que tudo foi inicializado
    const timer = setTimeout(() => {
      initializeDataSource(params, query);
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [location, searchParams]); // Removido params e query das dependências para evitar loops

  // Recuperar dados do layout quando studyInstanceUIDs estiver disponível
  useEffect(() => {
    const retrieveLayoutData = async () => {
      if (!studyInstanceUIDs?.length || studyInstanceUIDs[0] === undefined) {
        return;
      }

      try {
        const layoutTemplateData = await route.layoutTemplate({
          location,
          servicesManager,
          studyInstanceUIDs,
        });

        if (isMounted.current) {
          setLayoutData(layoutTemplateData);
        }
      } catch (error) {
        console.error('Erro ao recuperar dados do layout:', error);
      }
    };

    retrieveLayoutData();
  }, [studyInstanceUIDs, location]); // Dependências específicas

  // Configurar hotkeys
  useEffect(() => {
    if (!hotkeys) {
      return;
    }

    hotkeysManager.setDefaultHotKeys(hotkeys);

    const userPreferredHotkeys = JSON.parse(localStorage.getItem(hotkeyName));

    if (userPreferredHotkeys?.length) {
      hotkeysManager.setHotkeys(userPreferredHotkeys, hotkeyName);
    } else {
      hotkeysManager.setHotkeys(hotkeys, hotkeyName);
    }

    return () => {
      hotkeysManager.destroy();
    };
  }, []); // Executar apenas uma vez

  // Inicialização principal do modo
  useEffect(() => {
    if (!layoutData || !studyInstanceUIDs?.length) {
      return;
    }

    // TODO: For some reason this is running before the Providers
    // are calling setServiceImplementation
    // TODO -> iterate through services.

    // Extension

    // Add SOPClassHandlers to a new SOPClassManager.
    displaySetService.init(extensionManager, sopClassHandlers);

    extensionManager.onModeEnter({
      servicesManager,
      extensionManager,
      commandsManager,
    });

    // use the URL hangingProtocolId if it exists, otherwise use the one
    // defined in the mode configuration
    const hangingProtocolIdToUse = hangingProtocolService.getProtocolById(
      runTimeHangingProtocolId
    )
      ? runTimeHangingProtocolId
      : hangingProtocol;

    // Sets the active hanging protocols - if hangingProtocol is undefined,
    // resets to default.  Done before the onModeEnter to allow the onModeEnter
    // to perform custom hanging protocol actions
    hangingProtocolService.setActiveProtocolIds(hangingProtocolIdToUse);

    mode?.onModeEnter({
      servicesManager,
      extensionManager,
      commandsManager,
    });

    const setupRouteInit = async () => {
      /**
       * The next line should get all the query parameters provided by the URL
       * - except the StudyInstanceUIDs and token - and create an object called filters
       * used to filtering the study as the user wants otherwise it will return
       * a empty object.
       */
      const filters =
        Array.from(query.keys()).reduce(
          (acc: Record<string, string>, val: string) => {
            // Excluir StudyInstanceUIDs e token dos filtros
            if (!['StudyInstanceUIDs', 'token'].includes(val)) {
              if (['seriesInstanceUID', 'SeriesInstanceUID'].includes(val)) {
                return {
                  ...acc,
                  seriesInstanceUID: query.get(val),
                };
              }

              return { ...acc, [val]: query.get(val) };
            }
          },
          {}
        ) ?? {};

      if (route.init) {
        return await route.init(
          {
            servicesManager,
            extensionManager,
            hotkeysManager,
            studyInstanceUIDs,
            dataSource,
            filters,
          },
          hangingProtocolIdToUse
        );
      }

      return defaultRouteInit(
        {
          servicesManager,
          studyInstanceUIDs,
          dataSource,
          filters,
        },
        hangingProtocolIdToUse
      );
    };

    let unsubscriptions;
    setupRouteInit().then(unsubs => {
      unsubscriptions = unsubs;
    });

    return () => {
      // The mode.onModeExit must be done first to allow it to store
      // information, and must be in a try/catch to ensure subscriptions
      // are unsubscribed.
      try {
        mode?.onModeExit?.({
          servicesManager,
          extensionManager,
        });
      } catch (e) {
        console.warn('mode exit failure', e);
      }
      // The unsubscriptions must occur before the extension onModeExit
      // in order to prevent exceptions during cleanup caused by spurious events
      if (unsubscriptions) {
        unsubscriptions.forEach(unsub => {
          unsub();
        });
      }
      // The extension manager must be called after the mode, this is
      // expected to cleanup the state to a standard setup.
      extensionManager.onModeExit();
    };
  }, [
    layoutData,
    studyInstanceUIDs,
    mode,
    dataSourceName,
    location,
    route,
    servicesManager,
    extensionManager,
    hotkeysManager,
  ]); // Removido refresh das dependências

  const renderLayoutData = props => {
    const layoutTemplateModuleEntry = extensionManager.getModuleEntry(
      layoutData.id
    );
    const LayoutComponent = layoutTemplateModuleEntry.component;

    return <LayoutComponent {...props} />;
  };

  return (
    <ImageViewerProvider
      StudyInstanceUIDs={studyInstanceUIDs}
    >
      <CombinedContextProvider>
        <DragAndDropProvider>
          {layoutData &&
            studyInstanceUIDs?.length &&
            studyInstanceUIDs[0] !== undefined &&
            studyInstanceUIDs[0] !== null &&
            studyInstanceUIDs[0] !== '' &&
            renderLayoutData({
              ...layoutData.props,
              ViewportGridComp: ViewportGridWithDataSource,
            })}
          {(!studyInstanceUIDs || studyInstanceUIDs.length === 0) && (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              
            </div>
          )}
        </DragAndDropProvider>
      </CombinedContextProvider>
    </ImageViewerProvider>
  );
}

ModeRoute.propTypes = {
  mode: PropTypes.object.isRequired,
  dataSourceName: PropTypes.string,
  extensionManager: PropTypes.object,
  servicesManager: PropTypes.object,
  hotkeysManager: PropTypes.object,
};
